/**
 * The scheduled jobs of one or more services (`services/<svc>/.flow/jobs.json`, vfs-service src/appconfig), the list
 * the Jobs tab shows (JobsPanel): the service, the schedule in words, the next run, the last result, paused; Run now;
 * the last 20 runs with their logs (editors; the service's secrets appear as [secret NAME]); add (picking the service),
 * edit, pause or resume, delete. The jobs run only while the sandbox is awake: a sleeping sandbox runs nothing, and
 * missed times are not run later. The note says so, always in view.
 *
 * `entries`: `[{ service, jobs }]`, one per service whose jobs are known; `services`: every service a job can be added
 * to; `onChanged(service)`: read that service's jobs again (after a change, and while one of its jobs runs).
 */
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { PauseIcon, PencilSimpleIcon, PlayIcon, SparkleIcon, TrashIcon } from '@phosphor-icons/react';
import DataTable from '../table/DataTable';
import { IconButton } from '../table/TableParts';
import { deleteJob, getRun, listRuns, runJob, saveJob } from '../../services/AppConfigService';
import { RUN_LABEL, RUN_TONE, SCHEDULE_EXAMPLES, errorText, formToJob, jobProblem, jobToForm, relative, when } from './appConfigModel';

/** While a job runs (after Run now, or on schedule), its service's jobs are read again this often, this many times at most. */
export const JOB_POLL_MS = 1500;
export const JOB_POLL_TRIES = 80;

const isRunning = (job) => Boolean(job.running) || job.lastRun?.status === 'running';

function JobForm({ initial = null, services = [], service = null, onCancel, onSave, busy }) {
  const [form, setForm] = useState(() => jobToForm(initial));
  const [target, setTarget] = useState(service || services[0] || '');
  const [problem, setProblem] = useState(null);
  const set = (key) => (event) => setForm({ ...form, [key]: event.target.type === 'checkbox' ? event.target.checked : event.target.value });
  const submit = async(event) => {
    event.preventDefault();
    if (!target) { setProblem('Pick the service the job belongs to.'); return; }
    const why = jobProblem(form);
    if (why) { setProblem(why); return; }
    setProblem(await onSave(target, formToJob(form)));
  };
  return (
    <form className="cfg-job-form" data-testid="cfg-job-form" onSubmit={submit}>
      {!initial && (
        <label>Service
          <select className="cfg-input" onChange={(e) => setTarget(e.target.value)} value={target}>
            {services.map((name) => <option key={name} value={name}>{name}</option>)}
          </select>
        </label>
      )}
      <label>Name<input className="cfg-input cfg-mono" disabled={Boolean(initial)} onChange={set('name')} placeholder="nightly-report" value={form.name} /></label>
      <label>Schedule (cron)
        <input className="cfg-input cfg-mono" list="cfg-schedules" onChange={set('schedule')} value={form.schedule} />
        <datalist id="cfg-schedules">{SCHEDULE_EXAMPLES.map((s) => <option key={s.value} label={s.label} value={s.value} />)}</datalist>
      </label>
      <label>Time zone<input className="cfg-input" onChange={set('timezone')} placeholder="Europe/Rome" value={form.timezone} /></label>
      <fieldset className="cfg-kind">
        <legend>What it does</legend>
        <label><input checked={form.kind === 'command'} name="cfg-kind" onChange={() => setForm({ ...form, kind: 'command' })} type="radio" /> Run a command</label>
        <label><input checked={form.kind === 'http'} name="cfg-kind" onChange={() => setForm({ ...form, kind: 'http' })} type="radio" /> Call a path of the service</label>
      </fieldset>
      {form.kind === 'command'
        ? <label className="cfg-wide">Command<input className="cfg-input cfg-mono" onChange={set('command')} placeholder="node scripts/report.js" value={form.command} /></label>
        : (
          <div className="cfg-wide cfg-row">
            <label>Method
              <select className="cfg-input" onChange={set('method')} value={form.method}>
                {['GET', 'POST', 'PUT', 'PATCH', 'DELETE'].map((m) => <option key={m}>{m}</option>)}
              </select>
            </label>
            <label className="cfg-grow">Path<input className="cfg-input cfg-mono" onChange={set('path')} placeholder="/internal/cleanup" value={form.path} /></label>
          </div>
        )}
      <label>Time limit (s)<input className="cfg-input" max={3600} min={1} onChange={set('timeoutSeconds')} type="number" value={form.timeoutSeconds} /></label>
      <label className="cfg-check"><input checked={form.enabled} onChange={set('enabled')} type="checkbox" /> On</label>
      <div className="cfg-wide cfg-row">
        <button className="cfg-btn is-sm is-primary" disabled={busy} type="submit">{initial ? 'Save job' : 'Add job'}</button>
        <button className="cfg-btn is-sm is-quiet" onClick={onCancel} type="button">Cancel</button>
      </div>
      {problem && <p className="cfg-error cfg-wide" role="alert">{problem}</p>}
    </form>
  );
}

JobForm.propTypes = {
  busy: PropTypes.bool,
  initial: PropTypes.object,
  onCancel: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  service: PropTypes.string,
  services: PropTypes.arrayOf(PropTypes.string),
};

function Runs({ projectId, service, job, canEdit, onClose }) {
  const [runs, setRuns] = useState(null);
  const [open, setOpen] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    let live = true;
    listRuns(projectId, service, job.name).then((r) => { if (live) { setRuns(r.runs || []); } }).catch((e) => { if (live) { setError(e); } });
    return () => { live = false; };
  }, [projectId, service, job.name, job.running, job.lastRun?.status, job.lastRun?.startedAt]);
  const show = async(run) => {
    if (!canEdit) { return; }
    try { setOpen(await getRun(projectId, service, job.name, run.runId)); } catch (e) { setError(e); }
  };
  return (
    <div className="cfg-runs" data-testid="cfg-runs">
      <div className="cfg-row"><strong>Last runs of {job.name}</strong><button className="cfg-btn is-sm is-quiet" onClick={onClose} type="button">Close</button></div>
      {error && <p className="cfg-error" role="alert">{errorText(error)}</p>}
      {runs && runs.length === 0 && <p className="cfg-empty">No runs yet.</p>}
      {runs && runs.length > 0 && (
        <ul className="cfg-run-list">
          {runs.map((r) => (
            <li key={r.runId}>
              <button className="cfg-run" disabled={!canEdit || !r.hasLog} onClick={() => show(r)} type="button">
                <span className={`cfg-pill is-xs is-${RUN_TONE[r.status] || 'info'}`}>{RUN_LABEL[r.status] || r.status}</span>
                <span>{when(r.startedAt)}</span>
                <span className="cfg-muted">{r.trigger === 'manual' ? `Run now${r.by ? ` by ${r.by}` : ''}` : 'on schedule'}</span>
                <span className="cfg-muted">{r.message || ''}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {!canEdit && <p className="cfg-muted cfg-small">Logs are visible to editors.</p>}
      {open && <pre className="cfg-log" data-testid="cfg-run-log">{open.log || '(no output)'}{open.logTruncated ? '\n… only the last 64 KB are kept' : ''}</pre>}
    </div>
  );
}

Runs.propTypes = {
  canEdit: PropTypes.bool,
  job: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  projectId: PropTypes.string.isRequired,
  service: PropTypes.string.isRequired,
};

const lastResultText = (job) => (isRunning(job) ? 'Running' : job.lastRun ? `${RUN_LABEL[job.lastRun.status] || job.lastRun.status}` : 'Never ran');
const JOB_COLUMNS = [
  { key: 'service', label: 'Service', width: 120, className: 'cfg-mono', value: ({ service }) => service },
  {
    key: 'job',
    label: 'Job',
    value: ({ job }) => job.name,
    title: ({ job }) => `${job.name}: ${job.command || `${job.method} ${job.path}`}`,
    render: ({ job }) => (
      <>
        <div className="cfg-mono dt-line">{job.name}</div>
        <div className="cfg-muted cfg-small cfg-mono dt-line">{job.command || `${job.method} ${job.path}`}</div>
      </>
    ),
  },
  {
    key: 'when',
    label: 'When',
    width: 170,
    value: ({ job }) => job.describe,
    title: ({ job }) => `${job.describe} (${job.schedule} · ${job.timezone})`,
    render: ({ job }) => (
      <>
        <div className="dt-line">{job.describe}</div>
        <div className="cfg-muted cfg-small dt-line"><span className="cfg-mono">{job.schedule}</span> · {job.timezone}</div>
      </>
    ),
  },
  {
    key: 'next',
    label: 'Next run',
    width: 100,
    value: ({ job }) => (job.enabled ? job.nextRunAt || null : null),
    render: ({ job }) => (job.enabled ? (job.nextRunAt ? <span title={when(job.nextRunAt)}>{relative(job.nextRunAt)}</span> : '—') : <span className="cfg-pill is-info is-xs">Paused</span>),
  },
  {
    key: 'last',
    label: 'Last result',
    width: 140,
    value: ({ job }) => job.lastRun?.startedAt || null,
    title: ({ job }) => job.lastRun?.message || undefined,
    render: ({ job }) => (
      <>
        {isRunning(job) && <span className="cfg-pill is-prog is-xs">Running</span>}
        {!isRunning(job) && job.lastRun && <span className={`cfg-pill is-xs is-${RUN_TONE[job.lastRun.status] || 'info'}`} title={job.lastRun.message || ''}>{lastResultText(job)} · {relative(job.lastRun.startedAt)}</span>}
        {!isRunning(job) && !job.lastRun && <span className="cfg-muted">Never ran</span>}
      </>
    ),
  },
];
const keyOf = (service, name) => `${service}\u0000${name}`;

export default function ScheduledJobs({ projectId, entries, services = [], canEdit, onChanged, onAskAI = null, note = null }) {
  const [editing, setEditing] = useState(null); // null | 'new' | the key of a job
  const [runsOf, setRunsOf] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  const rows = entries.flatMap((entry) => (entry.jobs || []).map((job) => ({ service: entry.service, job })));

  // A run that finished shows its result without leaving the tab: while one runs, its service's jobs are read again.
  const runningServices = [...new Set(rows.filter((row) => isRunning(row.job)).map((row) => row.service))];
  const runningKey = runningServices.join(',');
  const tries = useRef(0);
  const onChangedRef = useRef(onChanged);
  onChangedRef.current = onChanged;
  useEffect(() => {
    if (!runningKey) { tries.current = 0; return undefined; }
    if (tries.current >= JOB_POLL_TRIES) { return undefined; }
    const timer = setTimeout(() => { tries.current += 1; runningKey.split(',').forEach((service) => onChangedRef.current(service)); }, JOB_POLL_MS);
    return () => clearTimeout(timer);
  }, [runningKey, entries]);

  const save = async(service, job) => {
    setBusy(true);
    try {
      await saveJob(projectId, service, job);
      setEditing(null);
      setNotice({ tone: 'pass', text: `Job ${job.name} saved.` });
      onChanged(service);
      return null;
    } catch (e) {
      return errorText(e);
    } finally {
      setBusy(false);
    }
  };
  const act = async(service, fn, text) => {
    setBusy(true);
    try {
      await fn();
      setNotice({ tone: 'pass', text });
      onChanged(service);
    } catch (e) {
      setNotice({ tone: 'fail', text: errorText(e) });
    } finally {
      setBusy(false);
    }
  };
  // Pause and Resume save the job as it is, switched off or on.
  const togglePaused = (service, job) => act(
    service,
    () => saveJob(projectId, service, { ...formToJob(jobToForm(job)), enabled: !job.enabled }),
    job.enabled ? `${job.name} paused: it does not run on its schedule until you resume it.` : `${job.name} resumed.`,
  );

  return (
    <section aria-labelledby="cfg-jobs-title" className="cfg-card" data-testid="cfg-jobs">
      <div className="cfg-card-head">
        <h3 id="cfg-jobs-title">Scheduled jobs</h3>
        <p className="cfg-muted">Run a command or call a path of a service on a schedule. Saved in each service&apos;s <code>.flow/jobs.json</code>; in a delivery each job becomes a Kubernetes CronJob.</p>
      </div>
      <p className="cfg-banner is-warn" data-testid="cfg-jobs-sleep">{note || 'Scheduled jobs run only while your sandbox is awake.'}</p>
      {notice && <p className={`cfg-banner is-${notice.tone}`} role="status">{notice.text}</p>}
      {rows.length === 0 && editing !== 'new' && <p className="cfg-empty">No scheduled jobs yet.</p>}
      {rows.length > 0 && (
        <DataTable
          actions={{
            label: 'Actions',
            width: canEdit ? 260 : 80,
            render: ({ service, job }) => {
              const key = keyOf(service, job.name);
              return (
                <span className="cfg-actions">
                  {canEdit && <button className="cfg-btn is-sm" disabled={busy || isRunning(job)} onClick={() => act(service, () => runJob(projectId, service, job.name), `${job.name} started. It runs in your sandbox now.`)} type="button">Run now</button>}
                  <button className="cfg-btn is-sm is-quiet" onClick={() => setRunsOf(runsOf === key ? null : key)} type="button">Runs</button>
                  {canEdit && <IconButton className="cfg-btn is-sm is-quiet is-icon" icon={<PencilSimpleIcon aria-hidden size={14} />} label="Edit" onClick={() => setEditing(key)} tip={`Edit ${job.name}`} />}
                  {canEdit && (
                    <IconButton
                      className="cfg-btn is-sm is-quiet is-icon"
                      disabled={busy}
                      icon={job.enabled ? <PauseIcon aria-hidden size={14} /> : <PlayIcon aria-hidden size={14} />}
                      label={job.enabled ? 'Pause' : 'Resume'}
                      onClick={() => togglePaused(service, job)}
                      tip={job.enabled ? 'Pause: it stops running on its schedule' : 'Resume its schedule'}
                    />
                  )}
                  {canEdit && <IconButton className="cfg-btn is-sm is-quiet is-icon" disabled={busy} icon={<TrashIcon aria-hidden size={14} />} label={`Delete job ${job.name}`} onClick={() => act(service, () => deleteJob(projectId, service, job.name), `Job ${job.name} removed.`)} tip="Delete" />}
                  {job.lastRun?.status === 'failed' && onAskAI && <IconButton className="cfg-btn is-sm is-quiet is-icon" icon={<SparkleIcon aria-hidden size={14} />} label="Fix with AI" onClick={() => onAskAI(`The scheduled job "${job.name}" of the service ${service} failed (${job.lastRun.message || 'see its log'}). Read its last run with vfs_jobs_list and fix it.`)} />}
                </span>
              );
            },
          }}
          after={({ service, job }, { colCount }) => {
            const key = keyOf(service, job.name);
            return (
              <>
                {editing === key && <tr><td colSpan={colCount}><JobForm busy={busy} initial={job} onCancel={() => setEditing(null)} onSave={save} service={service} /></td></tr>}
                {runsOf === key && <tr><td colSpan={colCount}><Runs canEdit={canEdit} job={job} onClose={() => setRunsOf(null)} projectId={projectId} service={service} /></td></tr>}
              </>
            );
          }}
          className="cfg-table"
          columns={JOB_COLUMNS}
          rowKey={({ service, job }) => keyOf(service, job.name)}
          rowProps={({ service, job }) => ({ 'data-testid': `cfg-job-${service}-${job.name}` })}
          rows={rows}
          storageKey="flow.jobs.hiddenColumns"
        />
      )}
      {canEdit && editing === 'new' && <JobForm busy={busy} onCancel={() => setEditing(null)} onSave={save} services={services} />}
      {canEdit && editing === null && services.length > 0 && <div><button className="cfg-btn is-sm" onClick={() => setEditing('new')} type="button">Add job</button></div>}
    </section>
  );
}

ScheduledJobs.propTypes = {
  canEdit: PropTypes.bool,
  entries: PropTypes.arrayOf(PropTypes.shape({ service: PropTypes.string.isRequired, jobs: PropTypes.array })).isRequired,
  // The sleeping-sandbox note as vfs-service words it (`jobsNote`).
  note: PropTypes.string,
  onAskAI: PropTypes.func,
  onChanged: PropTypes.func.isRequired,
  projectId: PropTypes.string.isRequired,
  services: PropTypes.arrayOf(PropTypes.string),
};
